import { useEffect, useState } from "react";
import "./global.css";

import { BackgroundAudioProvider } from "./BackgroundAudio";
import { afterLoadThenIdle, navigatorConnection, shouldPreload } from "./idle";
import { Contact } from "./pages/Contact";
import { Home } from "./pages/Home";
import { Live } from "./pages/Live";
import { MusicVideo } from "./pages/MusicVideo";
import { Watch } from "./pages/Watch";
import { CITY_OF_THIRST_RELEASED } from "./release";
import { usePathname } from "./router";
// Ticket 001's mobile layer — nine files behind one entry point. Imported here
// rather than from a page because it overrides rules from global.css and from
// all five page stylesheets. It does not rely on landing last in the bundle:
// `bun dev` and `bun run build` disagree about that, so every selector in it
// carries a `:root` prefix and wins on specificity instead. The order of the
// nine, on the other hand, *is* load-bearing, which is why they are chained with
// `@import` in one file rather than listed here — see src/mobile/index.css.
import "./mobile/index.css";

const ROUTES: Record<string, () => React.ReactElement> = {
  "/": Home,
  "/watch": Watch,
  "/live": Live,
  "/contact": Contact,
  // /music-video autoplays the City of Thirst embed and is nothing else, so
  // before release the route does not exist at all — the lookup below falls
  // through to Home rather than to a player nobody linked to. See src/release.ts.
  ...(CITY_OF_THIRST_RELEASED ? { "/music-video": MusicVideo } : {}),
};

/** Trailing slashes and the old .html URLs both still resolve. */
function normalize(pathname: string) {
  const stripped = pathname.replace(/\.html$/, "").replace(/\/+$/, "");
  if (stripped === "" || stripped === "/index") return "/";
  return stripped;
}

export function App() {
  const pathname = usePathname();
  const route = normalize(pathname);
  const onLive = route === "/live";
  const Page = ROUTES[route] ?? Home;

  /**
   * /live is mounted off-screen while the landing page sits idle, and afterwards
   * only shown and hidden.
   *
   * The original reason is gone: the dates used to come from Bandsintown's
   * widget, which took ~900 ms inside its own script whatever we did, and the
   * prewarm existed to pay that where nobody was waiting. Ticket 032 replaced
   * the widget with markup built from data in the bundle, so the dates now cost
   * nothing at all.
   *
   * The prewarm stays for the reason that was always underneath it: the page's
   * sky is a large photograph, and `routePrewarm` decoding it during the idle
   * period is what makes /live paint opaque in its first frame instead of fading
   * up over half a second. See src/routePrewarm.ts and the `isWarm` check in
   * src/pages/Live.tsx.
   */
  const [liveMounted, setLiveMounted] = useState(onLive);

  useEffect(() => {
    if (liveMounted) return;
    if (!shouldPreload(navigatorConnection())) return;
    afterLoadThenIdle(() => setLiveMounted(true));
  }, [liveMounted]);

  return (
    // The provider owns the one `<audio id="bg-audio">` and renders it outside
    // the route swap below, so the loop keeps playing — and keeps its mute state
    // and position — while pages come and go. See src/BackgroundAudio.tsx.
    <BackgroundAudioProvider>
      {!onLive && <Page />}
      {liveMounted && (
        // `inert` is what makes the hidden copy harmless: /live renders the same
        // fixed-position nav and social links as every other page, and without
        // this they would be a second, invisible set of focus and screen-reader
        // targets sitting on top of the visible ones.
        <div className={onLive ? "route-host" : "route-host route-host-hidden"} inert={!onLive}>
          <Live />
        </div>
      )}
    </BackgroundAudioProvider>
  );
}

export default App;
